import { SetContentsModal } from '@src/Component/Modal/SetContentsModal';

import { syncFrom } from '@src/Redux/Module/Cart';
import { unwrapResult } from '@reduxjs/toolkit';

/**
 * @typedef {import('@src/Redux/Store/OnlineStore').OlsStore} OlsStore
 *
 * @typedef {import('@src/Repository/CartRepository').CartRepository} CartRepository
 *
 * @typedef {import('@src/Service/TemplateFactory').TemplateFactory} TemplateFactory
 * @typedef {import('@src/Service/LocationProxy').LocationProxy} LocationProxy
 *
 * @typedef {import('@src/Dto/Response/Api/PriceBreakdown').PriceBreakdown} PriceBreakdown
 * @typedef {import('@src/Dto/Response/Api/LineItem').LineItem} LineItemInfo
 */
export class LineItem {
    /**
     *
     * @param {LineItemInfo} lineItem
     * @param {OlsStore} store
     * @param {CartRepository} cartRepository
     * @param {TemplateFactory} templateFactory
     * @param {LocationProxy} location
     */
    constructor(lineItem, store, cartRepository, templateFactory, location) {
        this._lineItem = lineItem;
        this._store = store;
        this._cartRepository = cartRepository;
        this._templateFactory = templateFactory;
        this._location = location;

        this._setContentsModal = new SetContentsModal();

        this._$element = this._templateFactory.render('line-item-template');
        this._populateElementData();
        this._adjustElementState();
        this._registerControls();
    }

    _populateElementData() {
        const $lineItem = this._$element;

        $lineItem?.attr('data-sell-name-encoded', this._lineItem.sellName);
        $lineItem?.find('.info .description').text(this._lineItem.description);
        $lineItem?.find('.quantity input').val(this._lineItem.quantity);
        $lineItem?.find('.price-breakdown .original .value').text(this._lineItem.totalPrice);
        $lineItem?.find('.price-breakdown .discounted .value').text(this._lineItem.totalDiscount);
        $lineItem?.find('.set-contents-trigger').attr('hidden', this._lineItem.setContents.length > 0 ? null : '');

        const $setContents = $lineItem?.find('.set-elements');
        const $lectures = $lineItem?.find('.webinar-lectures');

        this._lineItem.setContents.forEach((setItem) => {
            const $setItem = this._templateFactory.render('set-element-template');
            $setItem?.text(setItem.description);

            $setContents?.append($setItem);
        });

        this._lineItem.webinarLectures.forEach((lecture) => {
            const $lecture = this._templateFactory.render('webinar-lecture-template');
            $lecture?.attr('data-lecture-id', lecture.lectureId);
            $lecture?.find('.date').text(lecture.date);
            $lecture?.find('.time').text(lecture.time);
            $lecture?.find('.description').attr('href', lecture.link);
            $lecture?.find('.description').find('.label').text(lecture.title);

            $lectures?.append($lecture);
        });
    }

    _adjustElementState() {
        const $lineItem = this._$element;

        const onlyOne = this._lineItem.quantity === 1;
        const notMaxed = this._lineItem.quantity < 999;
        const adjustable = this._lineItem.isQuantityAdjustable;
        const removable = this._lineItem.isRemovable;

        const hasDiscount = this._lineItem.totalDiscount !== '0.00';

        $lineItem.find('.remove-all').attr('hidden', removable ? null : '');
        $lineItem.find('.quantity .remove').attr('hidden', onlyOne && removable ? null : '');
        $lineItem.find('.quantity .decrement').attr('hidden', !onlyOne && adjustable ? null : '');
        $lineItem.find('.quantity .increment').attr('hidden', notMaxed && adjustable ? null : '');
        $lineItem.find('.quantity input').attr('disabled', adjustable ? null : '');
        $lineItem.find('.price-breakdown .discounted').attr('hidden', hasDiscount ? null : '');
    }

    _registerControls() {
        const $lineItem = this._$element;
        const $setContents = $lineItem.find('.set-elements');

        $lineItem.find('.remove-all').on('click', () => this._removeFromCart());
        $lineItem.find('.quantity .remove').on('click', () => this._removeFromCart());
        $lineItem.find('.quantity .decrement').on('click', () => this._shiftQuantity(-1));
        $lineItem.find('.quantity .increment').on('click', () => this._shiftQuantity(1));
        $lineItem.find('.quantity input')
            .on('keydown', (event) => this._quantityInputMask(event))
            .on('change', (event) => this._updateQuantity(Number.parseInt($(event.target).val(), 10)));

        $lineItem.find('.set-contents-trigger').on('click', () => {
            this._setContentsModal.show($setContents);
        });

        $lineItem?.find('.webinar-lecture .remove').on('click', (event) => {
            const $lecture = $(event.currentTarget).parents('.webinar-lecture');
            const lectureId = $lecture.attr('data-lecture-id');

            this._store.dispatch(syncFrom(this._cartRepository.removeWebinar(lectureId)))
                .then(unwrapResult)
                .catch((error) => {
                    if (error.response.type?.endsWith('unauthorized')) {
                        window.location.href = '/store/signin.php';
                        return;
                    }

                    throw error;
                });
        });
    }

    _quantityInputMask(event) {
        if (event.key === 'Enter') {
            $(event.target).blur();
        } else if (event.key.match(/^\D$/)) {
            event.preventDefault();
        }
    }

    _shiftQuantity(n) {
        this._updateQuantity(this._lineItem.quantity + n);
    }

    async _updateQuantity(newQuantity) {
        // Display the most recent valid quantity if newQuantity is invalid.

        if (Number.isNaN(newQuantity) || newQuantity === undefined) {
            newQuantity = this._lineItem.quantity;
            this._$element.find('.quantity input').val(this._lineItem.quantity);
        } else {
            const updatePromise = (newQuantity === 0)
                ? this._cartRepository.removeLineItem(this._lineItem.sellName)
                : this._cartRepository.updateLineItem(this._lineItem.sellName, newQuantity);

            await this._store.dispatch(syncFrom(updatePromise)).then(unwrapResult);
        }
    }

    async _removeFromCart() {
        await this._store.dispatch(syncFrom(this._cartRepository.removeLineItem(this._lineItem.sellName)))
            .then(unwrapResult);
    }

    get $element() {
        return this._$element;
    }
}
